import type { StoreObject } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";

import SignupModal from "@/components/signup-modal";
import { Button } from "@/components/ui/button";
import useLeaveAllEscapesMutation from "@/graphql/mutations/use-leave-all-escapes";
import useRemoveSavedGameMutation from "@/graphql/mutations/use-remove-saved-game";
import useSaveGameMutation from "@/graphql/mutations/use-save-game";
import useUpdateEscapeMutation, {
  type EscapeType,
} from "@/graphql/mutations/use-update-escape";
import { cache } from "@/helpers/apollo";
import useAnalytics from "@/hooks/use-analytics";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import type { OverallRatingEnum } from "@/pages/search/types";

import { PlayedIcon, SaveIcon } from "./icons";

const BUTTON_CLASS_NAME = cn(
  "pointer-events-auto relative z-2 size-10 rounded-full bg-white/20 transition-colors"
);

export interface UserGameType {
  id: string;
  isCompleted?: boolean;
  isSaved?: boolean;
  name: string;
  overallRating?: keyof typeof OverallRatingEnum;
}

export interface UserActionsProps {
  game: UserGameType;
  onGameCompleted?: (escapeObj: EscapeType) => void;
}

export default function UserActions({
  game,
  onGameCompleted,
}: UserActionsProps) {
  const { track } = useAnalytics();
  const { isUser, mutationContext } = useAuth();
  const [signupModal, setSignupModal] = useState(false);

  const [leaveAllEscapes] = useLeaveAllEscapesMutation(
    mutationContext({ gameId: game.id })
  );
  const [removeSavedGame] = useRemoveSavedGameMutation(
    mutationContext({ gameId: game.id })
  );
  const [saveGame] = useSaveGameMutation(
    mutationContext({ input: { game: game.id } })
  );
  const [updateEscape, { data }] = useUpdateEscapeMutation(
    mutationContext({ escape: { game: game.id } })
  );

  const handleButton = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy
    (field: string) => () => {
      if (!isUser) {
        setSignupModal(true);
        return;
      }

      // biome-ignore lint/suspicious/noExplicitAny: not now bro
      const fields: { [field: string]: (value?: any) => any } = {};

      if (field === "isSaved") {
        const saved = !game.isSaved;
        fields.isSaved = () => saved;
        (saved ? saveGame : removeSavedGame)();
        track(saved ? "Game Added to Wishlist" : "Game Removed from Wishlist", {
          game_id: game.id,
          game_name: game.name,
          method: "swipe",
        });
      } else if (field === "isCompleted") {
        const completed = !game.isCompleted;
        fields.isCompleted = () => completed;
        fields.isSaved = () => false;
        fields.overallRating = () => null;
        (completed ? updateEscape : leaveAllEscapes)();
        track(completed ? "Experience Logged" : "Experience Removed", {
          game_id: game.id,
          game_name: game.name,
          method: "swipe",
        });
      }

      cache.modify({
        fields,
        id: cache.identify(game as unknown as StoreObject),
      });
    },
    [
      isUser,
      game,
      saveGame,
      removeSavedGame,
      updateEscape,
      leaveAllEscapes,
      track,
    ]
  );

  useEffect(() => {
    if (data?.updateEscape.escape) {
      cache.modify({
        fields: {
          myEscape: () => data.updateEscape.escape as EscapeType,
        },
        id: cache.identify(game as unknown as StoreObject),
      });

      if (typeof onGameCompleted === "function") {
        onGameCompleted(data.updateEscape.escape);
      }
    }
  }, [data, game, onGameCompleted]);

  return (
    <>
      <SignupModal onOpenChange={setSignupModal} open={signupModal} />
      <div className="pointer-events-none absolute inset-0 z-1 hidden items-center justify-center gap-4 opacity-0 transition-opacity group-hover:opacity-100 md:flex">
        {!game.isCompleted && (
          <Button
            aria-label="Wishlist"
            className={cn(
              BUTTON_CLASS_NAME,
              "hover:bg-berry/40 [&_svg_path]:transition-colors hover:[&_svg_path]:fill-berry hover:[&_svg_path]:stroke-berry",
              game.isSaved &&
                "bg-berry/40 [&_svg_path]:fill-berry [&_svg_path]:stroke-berry"
            )}
            onClick={handleButton("isSaved")}
          >
            <SaveIcon />
          </Button>
        )}
        <Button
          aria-label="Mark played"
          className={cn(
            BUTTON_CLASS_NAME,
            "hover:bg-teal/40 [&_svg_circle]:transition-colors hover:[&_svg_circle]:fill-teal hover:[&_svg_circle]:stroke-teal",
            game.isCompleted &&
              "bg-teal/40 [&_svg_circle]:fill-teal [&_svg_circle]:stroke-teal"
          )}
          onClick={handleButton("isCompleted")}
        >
          <PlayedIcon />
        </Button>
      </div>
    </>
  );
}
